import type {
  Clock,
  GameCommand,
  GameEvent,
  GameView,
  InputSource,
  Logger,
  RunnerSimulation,
} from '../core/types';
import type { DebugPanel, FrameDiagnostics } from '../debug/debug-panel';
import type { GameUi } from '../ui/game-ui';
import type { GameFeedback } from './game-feedback';

const MAX_FRAME_DELTA = 0.1;
const MAX_CATCH_UP_STEPS = 5;

export class GameApp {
  private readonly commandQueue: GameCommand[] = [];
  private frameHandle = 0;
  private previousTime = 0;
  private accumulator = 0;
  private running = false;
  private smoothedFrameTime = 16.7;
  private readonly frameDiagnostics: FrameDiagnostics = {
    fps: 60,
    frameTime: 16.7,
    fixedStepsLastFrame: 0,
  };

  public constructor(
    private readonly simulation: RunnerSimulation,
    private readonly view: GameView,
    private readonly input: InputSource,
    private readonly ui: GameUi,
    private readonly debug: DebugPanel,
    private readonly clock: Clock,
    private readonly logger: Logger,
    private readonly feedback: GameFeedback,
    private readonly fixedStep: number,
  ) {}

  public start(): void {
    if (this.running) return;
    this.running = true;
    this.previousTime = this.clock.now();
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.frameHandle = this.clock.requestFrame(this.frame);
    this.logger.info('game initialized', { seed: this.simulation.getSnapshot().seed });
  }

  public dispatch(command: GameCommand): void {
    this.feedback.unlock();
    this.commandQueue.push(command);
  }

  public restart(seed: string): void {
    this.simulation.reset(seed);
    this.accumulator = 0;
    this.commandQueue.length = 0;
  }

  public diagnostics(): Readonly<FrameDiagnostics> {
    return this.frameDiagnostics;
  }

  public dispose(): void {
    this.running = false;
    this.clock.cancelFrame(this.frameHandle);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.input.dispose();
    this.view.dispose();
    this.feedback.dispose();
  }

  private readonly frame = (time: number): void => {
    if (!this.running) return;
    const elapsedMilliseconds = Math.max(0, time - this.previousTime);
    this.previousTime = time;
    this.smoothedFrameTime = this.smoothedFrameTime * 0.9 + elapsedMilliseconds * 0.1;
    this.frameDiagnostics.frameTime = this.smoothedFrameTime;
    this.frameDiagnostics.fps = this.smoothedFrameTime > 0 ? 1000 / this.smoothedFrameTime : 60;
    this.accumulator += Math.min(MAX_FRAME_DELTA, elapsedMilliseconds / 1000);

    this.commandQueue.push(...this.input.drain());
    let steps = 0;
    while (this.accumulator >= this.fixedStep && steps < MAX_CATCH_UP_STEPS) {
      const commands: readonly GameCommand[] =
        steps === 0 ? this.commandQueue.splice(0, this.commandQueue.length) : [];
      const events = this.simulation.update(this.fixedStep, commands);
      this.feedback.handleEvents(events);
      this.logEvents(events);
      this.accumulator -= this.fixedStep;
      steps += 1;
    }
    if (steps === MAX_CATCH_UP_STEPS) this.accumulator = 0;
    this.frameDiagnostics.fixedStepsLastFrame = steps;

    const snapshot = this.simulation.getSnapshot();
    this.feedback.update(snapshot);
    const alpha = Math.min(1, this.accumulator / this.fixedStep);
    this.view.render(snapshot, alpha);
    this.ui.update(snapshot);
    this.debug.update(snapshot, this.frameDiagnostics);
    this.frameHandle = this.clock.requestFrame(this.frame);
  };

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') this.dispatch({ type: 'pause' });
  };

  private logEvents(events: readonly GameEvent[]): void {
    for (const event of events) {
      if (event.type === 'phaseChanged') {
        this.logger.info('phase changed', { from: event.from, to: event.to });
      } else if (event.type === 'collision') {
        this.logger.info('run ended', { obstacle: event.obstacle, entityId: event.entityId });
      }
    }
  }
}
